/**
 * Pure text helpers for the history panel (sprint 5d): one Serbian sentence
 * per audit entry, field labels and value formatting. All three take an
 * optional `t` so the panel can route through i18next while tests (and any
 * non-React caller) get the Serbian fallbacks directly.
 */
import { ATTRIBUTE_DATA_TYPE_LABEL } from "../api/catalog";
import { ASSET_CONDITION_LABEL } from "../api/assets";
import { POSITION_KIND_LABEL } from "../api/structure";
import type { HistoryAction, HistoryEntry } from "../api/history";
import { formatDate, formatDateTime } from "./dates";

export type Translate = (key: string, fallback: string) => string;

/** Fallback-only translator: what i18next returns for a missing key. */
const plain: Translate = (_key, fallback) => fallback;

/** Value shown for `null` / `undefined` / empty lists. */
export const EMPTY_VALUE = "—";

/**
 * Sentence stems per entity type and action. Main entities are "napravljen",
 * sub-entities (attribute, BOM item, node, attachment, note) are "dodat";
 * grammatical gender follows the Serbian noun.
 */
const SENTENCES: Record<string, Record<HistoryAction, string>> = {
  AssetType: { CREATE: "Napravljen tip", UPDATE: "Izmenjen tip", DELETE: "Obrisan tip", RESTORE: "Vraćen tip" },
  AttributeDef: { CREATE: "Dodat atribut", UPDATE: "Izmenjen atribut", DELETE: "Obrisan atribut", RESTORE: "Vraćen atribut" },
  // Type-level sastavnica (dropped in sprint 5l): the old audit rows still read in the type's history.
  AssetTypeBomItem: { CREATE: "Dodata stavka sastavnice", UPDATE: "Izmenjena stavka sastavnice", DELETE: "Obrisana stavka sastavnice", RESTORE: "Vraćena stavka sastavnice" },
  // Sastavnica of a position (sprint 5l): a line is removed physically, so DELETE reads "uklonjena".
  PositionBomItem: { CREATE: "Dodata stavka sastavnice", UPDATE: "Izmenjena stavka sastavnice", DELETE: "Uklonjena stavka sastavnice", RESTORE: "Vraćena stavka sastavnice" },
  Article: { CREATE: "Napravljen artikal", UPDATE: "Izmenjen artikal", DELETE: "Obrisan artikal", RESTORE: "Vraćen artikal" },
  ArticleType: { CREATE: "Napravljen tip artikla", UPDATE: "Izmenjen tip artikla", DELETE: "Obrisan tip artikla", RESTORE: "Vraćen tip artikla" },
  ArticleTypeAttribute: { CREATE: "Dodat atribut", UPDATE: "Izmenjen atribut", DELETE: "Obrisan atribut", RESTORE: "Vraćen atribut" },
  Asset: { CREATE: "Napravljen asset", UPDATE: "Izmenjen asset", DELETE: "Obrisan asset", RESTORE: "Vraćen asset" },
  Position: { CREATE: "Napravljena pozicija", UPDATE: "Izmenjena pozicija", DELETE: "Obrisana pozicija", RESTORE: "Vraćena pozicija" },
  Supplier: { CREATE: "Dodat dobavljač", UPDATE: "Izmenjen dobavljač", DELETE: "Obrisan dobavljač", RESTORE: "Vraćen dobavljač" },
  Attribute: { CREATE: "Dodat atribut u šifarnik", UPDATE: "Izmenjen atribut u šifarniku", DELETE: "Obrisan atribut iz šifarnika", RESTORE: "Vraćen atribut u šifarnik" },
  Unit: { CREATE: "Dodata jedinica mere", UPDATE: "Izmenjena jedinica mere", DELETE: "Obrisana jedinica mere", RESTORE: "Vraćena jedinica mere" },
  Location: { CREATE: "Napravljena lokacija", UPDATE: "Izmenjena lokacija", DELETE: "Obrisana lokacija", RESTORE: "Vraćena lokacija" },
  Template: { CREATE: "Napravljen šablon", UPDATE: "Izmenjen šablon", DELETE: "Obrisan šablon", RESTORE: "Vraćen šablon" },
  TemplateNode: { CREATE: "Dodat čvor šablona", UPDATE: "Izmenjen čvor šablona", DELETE: "Obrisan čvor šablona", RESTORE: "Vraćen čvor šablona" },
  Attachment: { CREATE: "Dodat prilog", UPDATE: "Izmenjen prilog", DELETE: "Obrisan prilog", RESTORE: "Vraćen prilog" },
  Comment: { CREATE: "Dodata napomena", UPDATE: "Izmenjena napomena", DELETE: "Obrisana napomena", RESTORE: "Vraćena napomena" },
  Layout: { CREATE: "Dodat layout", UPDATE: "Izmenjen layout", DELETE: "Obrisan layout", RESTORE: "Vraćen layout" },
  LayoutMarker: { CREATE: "Postavljena na layout", UPDATE: "Pomerena na layoutu", DELETE: "Uklonjena sa layouta", RESTORE: "Vraćena na layout" },
};

/** Neutral stems for entity types the map does not know; the type name is appended. */
const GENERIC: Record<HistoryAction, string> = { CREATE: "Napravljeno", UPDATE: "Izmenjeno", DELETE: "Obrisano", RESTORE: "Vraćeno" };

/** "Izmenjen atribut „Snaga (kW)“" — action + entity type + optional label. */
export function describeEntry(entry: Pick<HistoryEntry, "action" | "entityType" | "entityLabel">, t: Translate = plain): string {
  const known = SENTENCES[entry.entityType];
  const stem = known
    ? t(`history.sentence.${entry.entityType}.${entry.action}`, known[entry.action])
    : `${t(`history.sentence.generic.${entry.action}`, GENERIC[entry.action])} ${entry.entityType}`;
  const label = entry.entityLabel?.trim();
  return label ? `${stem} „${label}“` : stem;
}

const FIELD_LABELS: Record<string, string> = {
  code: "Šifra",
  name: "Naziv",
  description: "Opis",
  key: "Ključ",
  label: "Naziv",
  dataType: "Tip podatka",
  unit: "Jedinica",
  typeName: "Tip artikla",
  unitId: "Jedinica",
  symbol: "Oznaka",
  enumValues: "Vrednosti",
  enumOptions: "Vrednosti liste",
  required: "Obavezno",
  sortOrder: "Redosled",
  note: "Napomena",
  deletedReason: "Razlog brisanja",
  // Manual corrections in the database carry their reason in the audit row (docs/deploy/pcelica.md).
  reason: "Razlog izmene",
  lat: "Geo. širina",
  lng: "Geo. dužina",
  kind: "Vrsta",
  parentId: "Roditelj",
  expectedTypeId: "Očekivani tip",
  referenceValues: "Referentne vrednosti",
  interventionNote: "Napomena za intervenciju",
  drawingCode: "Šifra crteža",
  sizeBytes: "Veličina (B)",
  shape: "Oblik",
  x: "X",
  y: "Y",
  w: "Širina",
  h: "Visina",
  points: "Tačke",
  parts: "Delovi",
  modelParts: "Delovi modela",
  modelTriangles: "Trouglova",
  tag: "Oznaka",
  serialNo: "Serijski broj",
  condition: "Stanje",
  manufacturer: "Proizvođač",
  manufacturerCode: "Oznaka proizvođača",
  supplier: "Dobavljač",
  taxId: "PIB",
  registrationNo: "Matični broj",
  city: "Mesto",
  phone: "Telefon",
  email: "E-mail",
  contactPerson: "Kontakt osoba",
  isCustomMade: "Sopstvena izrada",
  wearLevel: "Pohabanost",
  workingHours: "Radni sati",
  filename: "Fajl",
  body: "Tekst",
  quantity: "Količina",
  locationId: "Lokacija",
  positionId: "Pozicija",
  assetTypeId: "Tip dela",
  childTypeId: "Tip dela",
  articleId: "Artikal",
  articleTypeId: "Tip artikla",
  attributeValues: "Vrednosti atributa",
  purchasedAt: "Datum nabavke",
  installedAt: "Datum ugradnje",
  warrantyUntil: "Garancija do",
  contentType: "Vrsta fajla",
  size: "Veličina",
  icon: "Ikonica",
  behavior: "Ponašanje",
  address: "Adresa",
};

/** Serbian label for a snapshot field; unknown fields show the raw name. */
export function fieldLabel(field: string, t: Translate = plain): string {
  const known = FIELD_LABELS[field];
  return known ? t(`history.field.${field}`, known) : field;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** Coded values get their catalogue labels where the field says which catalogue. */
function codedLabel(field: string, value: string, t: Translate): string | null {
  if (field === "dataType" && value in ATTRIBUTE_DATA_TYPE_LABEL) {
    return t(`attributeDefs.dataType.${value}`, ATTRIBUTE_DATA_TYPE_LABEL[value as keyof typeof ATTRIBUTE_DATA_TYPE_LABEL]);
  }
  if (field === "condition" && value in ASSET_CONDITION_LABEL) {
    return t(`assets.condition.${value}`, ASSET_CONDITION_LABEL[value as keyof typeof ASSET_CONDITION_LABEL]);
  }
  if (field === "kind" && value in POSITION_KIND_LABEL) {
    return t(`structure.kind.${value}`, POSITION_KIND_LABEL[value as keyof typeof POSITION_KIND_LABEL]);
  }
  return null;
}

/**
 * Raw snapshot value → display text: booleans "da"/"ne", lists joined with a
 * comma, objects one "key: value" per line, null "—", ISO dates in Serbian
 * format, coded fields (dataType, condition, kind) through their labels.
 */
export function formatValue(field: string, value: unknown, t: Translate = plain): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === "boolean") return value ? t("history.value.yes", "da") : t("history.value.no", "ne");
  if (Array.isArray(value)) {
    return value.length ? value.map((v) => formatValue(field, v, t)).join(", ") : EMPTY_VALUE;
  }
  if (typeof value === "object") {
    // A list option of an attribute (sprint 5f §6) reads as it is picked: "B3 — nožice".
    const o = value as Record<string, unknown>;
    if (typeof o.value === "string" && "description" in o && Object.keys(o).length === 2) {
      return o.description ? `${o.value} — ${String(o.description)}` : o.value;
    }
    // A span (RANGE, sprint 5f §7) reads as it is shown: "100 – 150", "≥ 100", "≤ 150".
    if (("min" in o || "max" in o) && Object.keys(o).every((k) => k === "min" || k === "max")) {
      if (o.min != null && o.max != null) return `${o.min} – ${o.max}`;
      if (o.min != null) return `≥ ${o.min}`;
      if (o.max != null) return `≤ ${o.max}`;
      return EMPTY_VALUE;
    }
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length
      ? entries.map(([k, v]) => `${k}: ${formatValue(k, v, t)}`).join("\n")
      : EMPTY_VALUE;
  }
  if (typeof value === "string") {
    const coded = codedLabel(field, value, t);
    if (coded) return coded;
    if (ISO_DATE.test(value)) return formatDate(value);
    if (ISO_INSTANT.test(value)) return formatDateTime(value) || value;
    return value === "" ? EMPTY_VALUE : value;
  }
  return String(value);
}
